import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { I } from '../lib/icons';
import type { AssetType } from '../../shared/asset-constants';

// Shared sub-component für AssetCard Quick-Actions: Download (mit Preset-
// Optionen für images) + Open-Detail.
//
// Visibility-Pattern (doppelt-gated):
//   - Desktop+Maus (`md + hoverable`): 2 Hover-Icon-Buttons mit
//     `opacity-0 group-hover:opacity-100`. Quick-Download = original
//     (1-click, kein Sub-Popover für UX-Schnelligkeit).
//   - Touch-Tablet/Mobile (`<md` ODER `md + !hoverable`): "..."-Button öffnet
//     Popover-Menu mit Original/Web/Print/Open-Actions. Web+Print nur wenn
//     assetType==='img' (Sprint Quick-Wins Item 3 Req 25 — non-image zeigt
//     nur "Original" als download-option).
//
// Pattern aus react.md § "Touch-Tablet-Hover-Hole" + "Shared Primitive
// mit variablem Visibility: Append-not-Replace Trigger-Class Contract".
//
// `actionsActive === false` (= Selection-Mode aktiv) → NICHTS rendern.
// Selection-Checkbox bleibt einziger Action-Slot, kein visuelles Crowding.
//
// Popover liegt ALS sibling im group-wrapper (NICHT nested im
// overflow-hidden Thumb-Container) — Codex K2 fix für iPad/375px-clip.
//
// Download-Action: same-origin `<a href onClick stopPropagation>` Click,
// KEIN window.location, KEIN download-attr (Codex S1 — Server setzt
// Content-Disposition: attachment).
//
// Minimal-Modal-a11y-Contract aus react.md: role=menu + role=menuitem +
// Esc-Handler + Outside-Click + autofocus. Body-Scroll-Lock NICHT nötig
// (kein Backdrop, ist ein Popover).

type Props = {
  assetId: string;
  assetType: AssetType;
  /** Wenn false (Selection-Mode aktiv): NICHTS rendern. */
  actionsActive: boolean;
  /** Quick-Look öffnen (Session 30). Wenn gesetzt → Auge-Button. Stabiler Callback. */
  onQuickLook?: (id: string) => void;
};

const ICON_BUTTON_BASE_CLASS =
  'w-7 h-7 flex items-center justify-center rounded-md text-brand-green-700 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 focus-visible:ring-offset-1';

export function CardActions({ assetId, assetType, actionsActive, onQuickLook }: Props) {
  const [, navigate] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);

  // Esc + Outside-Click → Menu schließen. Cleanup im finally-Pfad.
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    }
    function onClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [menuOpen]);

  // Autofocus auf erstes Item nach Menu-Open (50ms damit transition mountet).
  useEffect(() => {
    if (!menuOpen) return;
    const id = setTimeout(() => firstItemRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [menuOpen]);

  if (!actionsActive) return null;

  const isImage = assetType === 'img';
  const downloadOriginalHref = `/api/assets/${assetId}/download`;
  const downloadWebHref = `/api/assets/${assetId}/download?preset=web`;
  const downloadPrintHref = `/api/assets/${assetId}/download?preset=print`;

  function handleOpenDetail(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen(false);
    navigate(`/assets/${assetId}`);
  }

  function stopPropagation(e: React.MouseEvent) {
    // Hover-Button Download: kein preventDefault (default = navigate to href
    // = browser-download trigger), aber stopPropagation damit Card-Click
    // (navigate / select) nicht parallel feuert.
    e.stopPropagation();
  }

  function handleMenuItemClick(e: React.MouseEvent) {
    // Sub-Menu items: stopPropagation + close menu (Browser handelt download).
    e.stopPropagation();
    setMenuOpen(false);
  }

  return (
    <>
      {/* Desktop+Maus Hover-Cluster — nur Quick-Download (original), unten rechts
          am Bild. `md:hoverable:flex` matched nur ≥md mit (hover:hover +
          pointer:fine). Detail öffnet der Karten-Klick; Preset-Choice via
          "..."-Menu (Touch) oder AssetDetail. pointer-events-auto, da die
          Karten-Positions-Ebene pointer-events-none ist. */}
      {/* Redesign S2 (2026-10-10): Hover-Aktionen gebündelt OBEN RECHTS
          (Schnellansicht · Download), links daneben der Favoriten-Stern
          (FavoriteStarButton, top-2 right-2). Oben links sitzt die Auswahl-
          Checkbox, daneben das StatusBadge (left-12). `right-11` = Platz für
          den Stern. */}
      <div className="pointer-events-auto absolute top-2 right-11 hidden md:hoverable:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {onQuickLook && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onQuickLook(assetId);
            }}
            aria-label="Schnellansicht"
            title="Schnellansicht (Leertaste)"
            className={`${ICON_BUTTON_BASE_CLASS} bg-white/90 hover:bg-white shadow-sm`}
          >
            <I.eye size={14} />
          </button>
      )}
        <a
          href={downloadOriginalHref}
          onClick={stopPropagation}
          rel="noopener"
          aria-label="Original herunterladen"
          title="Original herunterladen"
          className={`${ICON_BUTTON_BASE_CLASS} bg-white/90 hover:bg-white shadow-sm`}
        >
          <I.download size={14} />
        </a>
      </div>

      {/* Touch-Tablet/Mobile "..."-Trigger — visible außer auf Desktop+Maus ≥md.
          `bottom-2 right-2` (untere Ecke, fluchtet mit dem Desktop-Download-
          Button). Nie gleichzeitig mit der Selection-Checkbox sichtbar
          (Selection-Mode → actionsActive=false → CardActions rendert null) und
          die Checkbox sitzt oben, also kein Konflikt. pointer-events-auto wg.
          der pointer-events-none Positions-Ebene. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setMenuOpen((open) => !open);
        }}
        aria-label="Aktionen"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        className={`pointer-events-auto absolute bottom-2 right-2 md:hoverable:hidden ${ICON_BUTTON_BASE_CLASS} bg-white/90 hover:bg-white shadow-sm`}
      >
        <I.more size={14} />
      </button>

      {/* Popover-Menu — sibling von "..."-Button (NICHT nested im overflow-
          hidden Thumb-Container, Codex K2). Öffnet NACH OBEN (`bottom-10`), da
          der Trigger jetzt unten ankert. Web/Print nur für img-assets (Req 25). */}
      {menuOpen && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Asset-Aktionen"
          className="pointer-events-auto absolute bottom-10 right-2 z-20 bg-white rounded-md shadow-lg border border-ink-200 py-1 min-w-[180px]"
        >
          {onQuickLook && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(false);
                onQuickLook(assetId);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-green-700 hover:bg-brand-cream focus:bg-brand-cream focus:outline-none text-left"
            >
              <I.eye size={14} />
              Schnellansicht
            </button>
          )}
          <a
            ref={firstItemRef}
            href={downloadOriginalHref}
            role="menuitem"
            onClick={handleMenuItemClick}
            rel="noopener"
            className="flex items-center gap-2 px-3 py-2 text-sm text-brand-green-700 hover:bg-brand-cream focus:bg-brand-cream focus:outline-none"
          >
            <I.download size={14} />
            Original
          </a>
          {isImage && (
            <>
              <a
                href={downloadWebHref}
                role="menuitem"
                onClick={handleMenuItemClick}
                rel="noopener"
                className="flex items-center gap-2 px-3 py-2 text-sm text-brand-green-700 hover:bg-brand-cream focus:bg-brand-cream focus:outline-none"
              >
                <I.download size={14} />
                Web (1280px)
              </a>
              <a
                href={downloadPrintHref}
                role="menuitem"
                onClick={handleMenuItemClick}
                rel="noopener"
                className="flex items-center gap-2 px-3 py-2 text-sm text-brand-green-700 hover:bg-brand-cream focus:bg-brand-cream focus:outline-none"
              >
                <I.download size={14} />
                Print (3000px)
              </a>
            </>
          )}
          <div className="my-1 border-t border-ink-200" />
          <button
            type="button"
            role="menuitem"
            onClick={handleOpenDetail}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-brand-green-700 hover:bg-brand-cream focus:bg-brand-cream focus:outline-none text-left"
          >
            <I.open size={14} />
            Detail öffnen
          </button>
        </div>
      )}
    </>
  );
}
